import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgButton } from "@intelligems/ig-theme";
import { ExperienceDto, VariationDto } from "@intelligems/ig-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { useFileBuild } from "../../../../api/experience-files/file-build";
import { isDialogOpen, setDialog } from "../../../../store/slices/global";
import { VariationButtons } from "../VariationButtons";

const PREFIX = "DownloadPriceCsv";

const classes = {
  root: `${PREFIX}-root`,
  dialogTitle: `${PREFIX}-dialogTitle`,
  dialogText1: `${PREFIX}-dialogText1`,
  dialogText2: `${PREFIX}-dialogText2`,
  articleLink: `${PREFIX}-articleLink`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.root}`]: {
    transition: "0.2s",
    borderRadius: "4px",
    border: `1px solid ${theme.palette.gray.light}`,
    "&:hover, &:focus": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  [`& .${classes.dialogTitle}`]: {
    fontSize: "22px",
    fontWeight: 600,
  },
  [`& .${classes.dialogText1}`]: {
    marginTop: "15px",
    marginBottom: "15px",
    color: theme.palette.grey[900],
  },
  [`& .${classes.dialogText2}`]: {
    marginBottom: 15,
  },
  [`& .${classes.articleLink}`]: {
    textDecoration: "none",
    color: theme.palette.primary.main,
    "&:hover": {
      color: theme.palette.primary.dark,
    },
  },
}));

interface DownloadPriceCsvDialogProps {
  currentExperience: ExperienceDto;
}

export const DownloadPriceCsvDialog = ({
  currentExperience,
}: DownloadPriceCsvDialogProps) => {
  const DIALOG_NAME = "downloadPriceCsv";
  const dispatch = useDispatch();
  const isOpen = useSelector(isDialogOpen(DIALOG_NAME));
  const buildPriceCsvFile = useFileBuild();
  const [selectedTestGroup, setSelectedTestGroup] = useState<
    string | undefined
  >(undefined);

  const onDownloadClick = () => {
    const currentVariation = currentExperience.variations.find(
      (tg) => tg.id === selectedTestGroup
    );
    let variations: VariationDto[] = [];
    if (currentVariation) variations = [currentVariation];

    buildPriceCsvFile.mutate({
      body: {
        variations,
        selectedProducts: [],
        name: currentExperience.name,
        experienceId: currentExperience.id,
        forExternalPriceUpload: true,
      },
      pathParams: {
        type: "csv",
      },
      queryParams: undefined,
    });

    closeDialog();
  };

  const closeDialog = () => {
    dispatch(setDialog({ dialog: DIALOG_NAME, open: false }));
  };

  const selectedCard = (id: string): boolean => {
    return selectedTestGroup === id;
  };

  return (
    <StyledDialog
      open={isOpen}
      aria-labelledby="pricing-dialog"
      aria-describedby="pricing-dialog"
      onClose={closeDialog}
      maxWidth={"xs"}
      fullWidth={true}
      PaperProps={{ style: { borderRadius: 15 } }}
    >
      <Grid
        container
        direction={"column"}
        alignItems={"center"}
        textAlign={"center"}
      >
        <DialogTitle
          sx={{ width: "350px" }}
          id="dialogBox-title"
          className={classes.dialogTitle}
        >
          {"Download a CSV of Price Changes"}
        </DialogTitle>
        <DialogContent sx={{ width: "350px" }}>
          <DialogContentText
            className={classes.dialogText1}
            id="dialogBox-text-1"
          >
            Optional - Choose a test group for the prices. If none selected, max
            will be set.
          </DialogContentText>
          <VariationButtons
            selectedTestGroup={selectedTestGroup}
            setSelectedTestGroup={setSelectedTestGroup}
            selectedCard={selectedCard}
            rootClass={classes.root}
            variations={currentExperience.variations}
          />
        </DialogContent>
        <DialogActions>
          <Grid container direction={"column"}>
            <IgButton
              loading={buildPriceCsvFile.isPending}
              variant={"contained"}
              onClick={onDownloadClick}
            >
              {`Download CSV`}
            </IgButton>
            <IgButton loading={false} onClick={closeDialog}>
              Cancel
            </IgButton>
          </Grid>
        </DialogActions>
      </Grid>
    </StyledDialog>
  );
};

export default DownloadPriceCsvDialog;
